import { Form, Formik, FormikErrors } from 'formik';
import React from 'react';
import {
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooterDefault,
  ModalHeaderDefault,
  Paragraph,
  useSnackbar,
} from 'ui-components';
import { validateEmail } from '~/utils-ts';

interface FakeDoorFormValues {
  email: string;
}

interface FakeDoorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string) => void | Promise<void>;
}

const validate = ({ email }: FakeDoorFormValues) => {
  const errors: FormikErrors<FakeDoorFormValues> = {};

  if (!email) {
    errors.email = 'Email is required';
  } else if (!validateEmail(email, true)) {
    errors.email = 'Must be a valid email';
  }

  return errors;
};

export const FakeDoorModal: React.FC<FakeDoorModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const snackbar = useSnackbar();

  const handleSubmit = async (values: FakeDoorFormValues) => {
    try {
      await onSubmit?.(values.email);
      onClose();
    } catch (error) {
      snackbar.error({
        description: 'Something went wrong. Please try again later.',
      });
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Formik<FakeDoorFormValues>
      initialValues={{ email: '' }}
      onSubmit={handleSubmit}
      validate={validate}
      validateOnMount={false}
    >
      {({ values, errors, handleBlur, handleChange, isSubmitting }) => (
        <Modal isOpen={isOpen} onClose={handleClose} disableModalContent>
          <ModalContent as={Form}>
            <ModalHeaderDefault title="Thank you for your interest!" />

            <ModalBody>
              <div className="flex flex-col gap-4">
                <Paragraph size="md" color="neutral.600">
                  Foam for Marketers is coming soon. It's not quite ready, yet.
                  But sign up below and you will be the first to know.
                </Paragraph>
                <div className="flex flex-col gap-2">
                  <label htmlFor="email-input">Email</label>
                  <Input
                    error={errors.email}
                    id="email-input"
                    isRequired
                    name="email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.email}
                  />
                </div>
              </div>
            </ModalBody>

            <ModalFooterDefault
              primary={{
                ariaLabel: 'Request early access',
                type: 'submit',
                title: 'Request early access',
                isDisabled:
                  !values.email.trim() || !!errors.email || isSubmitting,
                isLoading: isSubmitting,
                size: 'md',
                sx: {
                  flex: 'auto',
                },
              }}
              secondary={{
                type: 'button',
                ariaLabel: 'Cancel',
                onClick: handleClose,
                title: 'Cancel',
                size: 'md',
                sx: {
                  flex: 'auto',
                },
              }}
            />
          </ModalContent>
        </Modal>
      )}
    </Formik>
  );
};
